import { Control } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Unit } from "@/types/types";

interface UnitSelectorProps {
  control: Control<any>;
  units: Unit[];
  disabled?: boolean;
  required?: boolean;
  showStatus?: boolean;
}

/**
 * Reusable UnitSelector component
 * Single responsibility: Handle unit selection from a list with optional status badges
 */
export function UnitSelector({
  control,
  units,
  disabled = false,
  required = false,
  showStatus = true,
}: UnitSelectorProps) {
  const sortedUnits = [...units].sort((a, b) => {
    if (a.floor !== b.floor) return a.floor - b.floor;
    return a.name.localeCompare(b.name);
  });

  return (
    <FormField
      control={control}
      name="unitId"
      render={({ field }) => (
        <FormItem>
          <FormLabel className="text-base">
            Unit {required ? "*" : "(Optional)"}
          </FormLabel>
          <Select
            onValueChange={(value) => {
              field.onChange(value === "none" ? "" : value);
            }}
            value={field.value || "none"}
            disabled={disabled}
          >
            <FormControl>
              <SelectTrigger className="w-full h-11">
                <SelectValue placeholder="Select a unit (optional)" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value="none">None (All Units)</SelectItem>
              {units.length === 0 ? (
                <div className="p-3 text-sm text-muted-foreground text-center">
                  No units available
                </div>
              ) : (
                sortedUnits.map((unit) => (
                  <SelectItem key={unit.id} value={unit.id}>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{unit.name}</span>
                      {showStatus && unit.status === "occupied" && (
                        <Badge variant="secondary" className="text-xs">
                          Occupied
                        </Badge>
                      )}
                      {showStatus && unit.status === "vacant" && (
                        <Badge variant="outline" className="text-xs">
                          Vacant
                        </Badge>
                      )}
                    </div>
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  );
}
